import { inject, Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormEditorService } from '@core/services/form-editor.service';
import { DynamicField } from '@shared/components/dynamic-form/models/field';
import { DynamicChoicesSettings } from '@shared/components/dynamic-form/models/field-dynamic-choices';
import { FIELD_TYPE } from '@shared/constants/field-type.const';
import { FormScore } from '../models/field-base';

@Injectable({
  providedIn: 'root',
})
export class DynamicFormService {
  private readonly fb = new FormBuilder();

  formEditorService = inject(FormEditorService);

  toFormGroup(fields: DynamicField[]) {
    const group: Record<string, FormControl | FormArray | FormGroup> = {};

    for (const field of fields) {
      if (field.controlType === FIELD_TYPE.DYNAMIC_CHOICES) {
        group[field.key] = this.fb.group({
          choices: [
            (field.settings as DynamicChoicesSettings)?.choice?.choices || 0,
          ],
          selections: this.fb.array([]),
        });
      } else {
        group[field.key] = new FormControl(field.value, field.validatorOrOpts);
      }
    }

    return new FormGroup(group);
  }

  getFormScore(form: FormGroup): FormScore {
    const formValue = form.getRawValue();
    let totalScore = 0;
    let currentSectionSum = 0;
    const sectionSummaries: Record<string, number> = {};

    for (const key of Object.keys(formValue)) {
      let weight: number;
      const field = this.formEditorService.getFieldByKey(key);
      if (!field) continue;

      const value = formValue[key];
      if (field.settings && 'weight' in field.settings) {
        weight = (field?.settings?.weight as number) ?? 1;
      }

      switch (field.controlType) {
        case FIELD_TYPE.STAR_RATING:
        case FIELD_TYPE.CHOICES_RATING:
        case FIELD_TYPE.YES_NO_RATING:
          currentSectionSum += +value * weight;
          break;

        case FIELD_TYPE.SECTION_SUMMARY: {
          const sectionScore = currentSectionSum * weight;
          sectionSummaries[key] = sectionScore;
          form.get(key)?.setValue(sectionScore, { emitEvent: false });
          currentSectionSum = 0;
          break;
        }

        default:
          break;
      }
    }

    if (currentSectionSum > 0) {
      totalScore += currentSectionSum;
    }

    for (const sectionKey in sectionSummaries) {
      totalScore += sectionSummaries[sectionKey];
    }

    return { sectionSummaries, totalScore };
  }
}
